import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getAllResultBreakdowns,
  createResultBreakdown,
  updateResultBreakdown,
  type ResultBreakdown,
  type CreateResultBreakdownData,
} from "../../../service/result-breakdown";
import { submitApproval } from "../../../service/approvals";
import { getAllTerms, getTermsForScope } from "../../../service/term-management";
import { getAllSessions, type Session } from "../../../service/sessions";
import { getAllClasses, type Class } from "../../../service/classes";
import { getAllSystems } from "../../../service/systems";
import { selectBranches, type Branch } from "../../../core/data/redux/slice/branchesSlice";
import { selectSystems } from "../../../core/data/redux/slice/systemsSlice";

// 2026-10-02: Term is now global (RMS-parity) -- one document, no more
// branch/session fields. Where it applies lives in TermScope instead;
// see termsByBranch below, built via getTermsForScope per ticked campus.
type Term = { _id: string; title: string; displayOrder?: number };

type System = { _id: string; name: string; code?: string };

// Fixed display/selection priority: Matric, O-Level, AKU, College.
const SYSTEM_PRIORITY: Record<string, number> = { MATRIC: 1, OLEVEL: 2, AKU: 3, COLLEGE: 4 };
const byPriority = (systems: System[]) =>
  [...systems].sort((a, b) => (SYSTEM_PRIORITY[a.code || ""] ?? 99) - (SYSTEM_PRIORITY[b.code || ""] ?? 99));

type StatusValue = "draft" | "active" | "submitted" | "needs_revision" | "approved" | "published" | "marks_locked" | "inactive";

const STATUS_OPTIONS: StatusValue[] = ["draft", "active", "submitted", "needs_revision", "approved", "published", "marks_locked", "inactive"];

type FormData = {
  sessionId: string;
  // Multi-select in "new" mode (creates one independent breakdown per
  // campus ticked); forced to a single, locked entry in "edit" mode (a
  // breakdown can't be moved to a different campus after creation).
  branchIds: string[];
  systemId: string;
  termId: string;
  startDate: string;
  endDate: string;
  // Classes are a shared catalog (scoped by Board System, not per campus)
  // -- the same Class _id list applies to every campus ticked.
  classIds: string[];
  title: string;
  titleOverridden: boolean;
  visibleInApp: boolean;
  status: StatusValue;
};

const EMPTY_FORM: FormData = {
  sessionId: "",
  branchIds: [],
  systemId: "",
  termId: "",
  startDate: "",
  endDate: "",
  classIds: [],
  title: "",
  titleOverridden: false,
  visibleInApp: false,
  status: "draft",
};

// ── Shared confirm modal (matches report-card-designer's pattern) ──────────
function ConfirmModal({ open, title, body, danger, onConfirm, onCancel }: {
  open: boolean; title: string; body: string; danger?: string
  onConfirm: () => void; onCancel: () => void
}) {
  if (!open) return null
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center p-4">
              <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
              <h4 className="mt-2">{title}</h4>
              <p className="text-muted">{body}</p>
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button className="btn btn-light" onClick={onCancel}>Cancel</button>
                <button className="btn btn-danger" onClick={onConfirm}>{danger || "Yes, Delete"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

const ResultBreakdowns = () => {
  const branches = useSelector(selectBranches) as Branch[];
  const reduxSystems = useSelector(selectSystems) as System[];

  // Master data
  const [terms, setTerms] = useState<Term[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [systems, setSystems] = useState<System[]>([]);

  // List + filters
  const [records, setRecords] = useState<ResultBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterSession, setFilterSession] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterSystem, setFilterSystem] = useState("");
  const [filterTerm, setFilterTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 20;

  // Modal state
  const [modalMode, setModalMode] = useState<"new" | "edit" | null>(null);
  const [editing, setEditing] = useState<ResultBreakdown | null>(null);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Filter row keeps the old Campus-picks-Board relationship (unchanged --
  // only the create/edit FORM's order + multi-campus behavior changed).
  const systemsForBranch = (branchId: string) => {
    const branch = branches.find((b) => b._id === branchId);
    const allowedIds = branch?.systems?.map((s) => s._id);
    const filtered = allowedIds?.length ? systems.filter((s) => allowedIds.includes(s._id)) : systems;
    return byPriority(filtered);
  };
  const systemsForFilter = useMemo(() => systemsForBranch(filterBranch), [branches, filterBranch, systems]);

  // Form order is now Board System BEFORE Campus, so Board no longer
  // depends on a chosen campus -- show every board up front.
  const systemsForForm = useMemo(() => byPriority(systems), [systems]);

  // Campus is now filtered by the chosen Board System (reversed direction
  // from before) -- only campuses whose own `systems` list includes it.
  // `branches` is already scoped to what THIS user is allowed to manage
  // (see bootstrapSession.ts) -- reused as-is, not a new access rule.
  const branchesForForm = useMemo(
    () => (form.systemId ? branches.filter(b => b.systems?.some(s => s._id === form.systemId)) : []),
    [branches, form.systemId]
  );

  // Classes are a SHARED catalog scoped only by Board System, NOT per
  // campus -- confirmed directly against real data: only 2 of 6 campuses
  // own any Class documents at all, the rest share the same ones via their
  // System. So the SAME Class _id list applies to every campus ticked; no
  // per-campus resolution needed (an earlier version of this tried
  // matching by name per-campus, solving a problem that doesn't exist).
  // `system` comes back POPULATED (an object, not a raw id) via the class
  // route's .populate("system", ...); classSystemId handles both shapes.
  const classSystemId = (c: Class): string => {
    const s = c.system as any;
    return s && typeof s === "object" ? s._id : s;
  };
  const classOptionsForForm = useMemo(() => {
    if (!form.systemId) return [];
    return classes.filter(c => classSystemId(c) === form.systemId);
  }, [classes, form.systemId]);

  // Same idea as before, for Session -- a separate document per campus,
  // sharing only its title. Only populated when sessions were fetched with
  // all:true (see the master-data load above).
  const sessionBranchId = (s: Session): string => {
    const b = (s as any).branch;
    return b && typeof b === "object" ? b._id : (b ?? "");
  };
  // Term is global now -- no branch/session to filter the list-page's own
  // Term dropdown by; just show every term, sorted the same way the backend
  // already orders them.
  const termsForFilter = useMemo(
    () => [...terms].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [terms]
  );

  // Session is a separate document per campus, so `sessions` (fetched with
  // all:true) has one entry per campus for the SAME title -- e.g. six
  // "Aug 2026-2027" rows. The dropdown only needs to offer the distinct
  // TITLE (that's all that's shared across campuses, and all handleSave
  // resolves by); which campus's underlying id backs each option is
  // irrelevant here, since every campus's own id is re-resolved by title
  // at save time anyway.
  const uniqueSessionsByTitle = useMemo(() => {
    const seen = new Set<string>();
    const out: Session[] = [];
    for (const s of sessions) {
      if (seen.has(s.title)) continue;
      seen.add(s.title);
      out.push(s);
    }
    return out;
  }, [sessions]);

  const referenceSessionTitle = sessions.find(s => s._id === form.sessionId)?.title || "";

  // Which global terms are actually usable for each ticked campus, under the
  // selected board system -- TermScope's answer, replacing yesterday's
  // title-matching workaround now that Term carries no branch/session at
  // all. Keyed by branch so handleSave can check "is this term scoped to
  // THIS campus" per campus without re-fetching at save time.
  const [termsByBranch, setTermsByBranch] = useState<Record<string, Term[]>>({});
  useEffect(() => {
    if (!form.systemId || form.branchIds.length === 0) {
      setTermsByBranch({});
      return;
    }
    let cancelled = false;
    Promise.all(
      form.branchIds.map(branchId =>
        getTermsForScope(branchId, form.systemId).then(list => [branchId, list] as const).catch(() => [branchId, []] as const)
      )
    ).then(entries => {
      if (cancelled) return;
      setTermsByBranch(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [form.systemId, form.branchIds]);

  // Term picker shows the UNION across ticked campuses (same spirit as
  // before: show it if at least one ticked campus can use it, then skip the
  // others individually at save time) -- deduped by _id, which is now
  // meaningful since a term really is one single document everywhere.
  const termsForForm = useMemo(() => {
    const map = new Map<string, Term>();
    for (const list of Object.values(termsByBranch)) for (const t of list) map.set(t._id, t);
    return Array.from(map.values()).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [termsByBranch]);

  // ── Load master data ─────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      // Term is global now -- a plain list, just for the list-page's own
      // Term filter dropdown (see termsForFilter). The create form resolves
      // its own options via TermScope (termsByBranch above).
      getAllTerms(1, 1000).then(r => setTerms(Array.isArray(r.data) ? r.data : [])),
      // all:true -- Session is a separate document PER CAMPUS (sharing only
      // its title, e.g. "Aug 2026-2027"), same as Term. Without this, only
      // the current campus's own sessions load, and every other ticked
      // campus's session (needed below to resolve ITS OWN matching term and
      // to save ITS OWN breakdown with the right sessionId) is invisible.
      getAllSessions({ page: 1, limit: 1000, all: true }).then(r => setSessions(r.sessions ?? [])),
      // all:"true" bypasses the single-campus Branch.systems scoping --
      // without it, the API silently narrows results to whatever branch
      // the request happens to resolve to, so ticking a DIFFERENT campus in
      // this page's own multi-select would show "No classes available"
      // even though that campus genuinely has classes.
      getAllClasses({ page: 1, limit: 1000, all: "true" }).then(r => setClasses(r.classes ?? [])),
      getAllSystems().then(r => setSystems(Array.isArray(r.data) ? r.data : reduxSystems)),
    ]).catch(() => {});
  }, []);

  // ── Fetch list ────────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAllResultBreakdowns({
        page,
        limit: LIMIT,
        session: filterSession || undefined,
        branchId: filterBranch || undefined,
        systemId: filterSystem || undefined,
        termId: filterTerm || undefined,
      });
      setRecords(Array.isArray(r.data) ? r.data : []);
      setTotalPages(r.pagination?.totalPages ?? 1);
    } catch {
      toast.error("Error fetching result breakdowns");
    } finally {
      setLoading(false);
    }
  }, [page, filterSession, filterBranch, filterSystem, filterTerm]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── Auto-generate title ───────────────────────────────────────────────────
  const autoTitle = useCallback((f: FormData): string => {
    const term = terms.find(t => t._id === f.termId);
    const session = sessions.find(s => s._id === f.sessionId);
    const classNames = f.classIds.map(id => classes.find(c => c._id === id)?.name).filter(Boolean);
    if (!term || !session || classNames.length === 0) return "";
    return `${term.title} (${classNames.join(", ")}) ${session.title}`;
  }, [terms, sessions, classes]);

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (!next.titleOverridden) next.title = autoTitle(next);
      return next;
    });
  };

  // ── Open modals ───────────────────────────────────────────────────────────
  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalMode("new");
  };

  // ── Siblings: other campuses' records from the SAME multi-campus create ───
  // There's no stored link between records created together via the "New"
  // multi-select -- each campus's breakdown is a fully independent document.
  // Approximated here instead: same global Term + same session TITLE + same
  // set of classes, different campus. Reliable because Term is now global
  // (one real id, not a per-campus duplicate) and the auto-generated title
  // pattern is identical for records ticked together -- a false match would
  // need an unrelated create to independently pick the exact same term,
  // session title, and class set for a different campus, which is rare and
  // harmless to show as "related" even if it happens.
  const [siblings, setSiblings] = useState<ResultBreakdown[]>([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);
  // Snapshot of which OTHER campuses already had this breakdown when the
  // modal opened -- diffed against form.branchIds at save time to know
  // which campuses to create (newly ticked) or deactivate (newly unticked).
  const [originalSiblingBranchIds, setOriginalSiblingBranchIds] = useState<string[]>([]);

  const loadSiblings = async (rec: ResultBreakdown) => {
    setSiblingsLoading(true);
    try {
      const r = await getAllResultBreakdowns({ termId: toId(rec.termId), limit: 200, all: true });
      const recClassIds = rec.classes.map(c => c._id).sort().join(",");
      const recSessionTitle = rec.session?.title || "";
      const matches = (Array.isArray(r.data) ? r.data : []).filter(other => {
        if (other._id === rec._id) return false;
        if (toId(other.branchId) === toId(rec.branchId)) return false;
        const otherClassIds = other.classes.map(c => c._id).sort().join(",");
        return otherClassIds === recClassIds && (other.session?.title || "") === recSessionTitle;
      });
      setSiblings(matches);
      const siblingBranchIds = matches.map(s => toId(s.branchId));
      setOriginalSiblingBranchIds(siblingBranchIds);
      // Campus checkboxes should reflect reality: every campus that already
      // has this breakdown starts ticked, not just the one being edited.
      setForm(prev => ({
        ...prev,
        branchIds: Array.from(new Set([toId(rec.branchId), ...siblingBranchIds])),
      }));
    } catch {
      setSiblings([]);
      setOriginalSiblingBranchIds([]);
    } finally {
      setSiblingsLoading(false);
    }
  };

  const openEdit = (rec: ResultBreakdown) => {
    setEditing(rec);
    const classIds = rec.classes.map((c: any) => (typeof c === "object" ? c._id : c));
    setForm({
      sessionId: toId(rec.sessionId),
      // Edit is always exactly this one campus -- locked, can't be widened
      // into a multi-campus edit (that's what "new" is for).
      branchIds: [toId(rec.branchId)],
      systemId: toId(rec.systemId),
      termId: toId(rec.termId),
      startDate: rec.startDate ? rec.startDate.split("T")[0] : "",
      endDate: rec.endDate ? rec.endDate.split("T")[0] : "",
      classIds,
      title: rec.title,
      titleOverridden: true,
      visibleInApp: (rec as any).visibleInApp ?? false,
      status: (rec.status as any) ?? "draft",
    });
    setModalMode("edit");
    loadSiblings(rec);
  };

  const closeModal = () => { setModalMode(null); setEditing(null); setSiblings([]); setOriginalSiblingBranchIds([]); };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.sessionId || !form.systemId || form.branchIds.length === 0 || !form.termId || form.classIds.length === 0) {
      toast.error("Session, Board system, at least one Campus, Term and at least one Class are required");
      return;
    }
    setSaving(true);
    try {
      if (modalMode === "edit" && editing) {
        const currentBranch = toId(editing.branchId);
        const payload: CreateResultBreakdownData & { visibleInApp?: boolean } = {
          title: form.title || autoTitle(form),
          sessionId: form.sessionId,
          // Always the record actually being edited -- NOT form.branchIds[0],
          // which may now be a sibling campus the admin just ticked.
          branchId: currentBranch,
          systemId: form.systemId,
          termId: form.termId,
          startDate: form.startDate,
          endDate: form.endDate,
          classes: form.classIds,
          status: form.status,
          visibleInApp: form.visibleInApp,
        } as any;
        await updateResultBreakdown(editing._id, payload);

        // Campus checkboxes, diffed against what was already there when the
        // modal opened: newly ticked campuses get their own new breakdown
        // (same term/classes/dates/title/status just saved above); newly
        // unticked siblings get deactivated. Each sibling's OWN independent
        // status/progress is otherwise left untouched.
        const sessionTitle = sessions.find(s => s._id === form.sessionId)?.title || "";
        const newlyAdded = form.branchIds.filter(b => b !== currentBranch && !originalSiblingBranchIds.includes(b));
        const newlyRemoved = originalSiblingBranchIds.filter(b => !form.branchIds.includes(b));
        const createdNames: string[] = [];
        const skippedNames: string[] = [];
        const deactivatedNames: string[] = [];

        for (const branchId of newlyAdded) {
          const branchName = branches.find(b => b._id === branchId)?.name || branchId;
          const ownSession = sessions.find(s => s.title === sessionTitle && sessionBranchId(s) === branchId);
          const scopedToThisBranch = (termsByBranch[branchId] || []).some(t => t._id === form.termId);
          if (!ownSession || !scopedToThisBranch) {
            skippedNames.push(branchName);
            continue;
          }
          try {
            await createResultBreakdown({
              title: form.title || autoTitle(form),
              sessionId: ownSession._id,
              branchId,
              systemId: form.systemId,
              termId: form.termId,
              startDate: form.startDate,
              endDate: form.endDate,
              classes: form.classIds,
              status: form.status,
              visibleInApp: form.visibleInApp,
            } as any);
            createdNames.push(branchName);
          } catch (err: any) {
            skippedNames.push(`${branchName} (${err?.response?.data?.message || "error"})`);
          }
        }
        for (const branchId of newlyRemoved) {
          const sibling = siblings.find(s => toId(s.branchId) === branchId);
          if (!sibling) continue;
          await updateResultBreakdown(sibling._id, { status: "inactive" } as any);
          deactivatedNames.push(branches.find(b => b._id === branchId)?.name || branchId);
        }

        toast.success("Breakdown updated");
        if (createdNames.length) toast.success(`Also created for: ${createdNames.join(", ")}`);
        if (deactivatedNames.length) toast.info(`Deactivated for: ${deactivatedNames.join(", ")}`);
        if (skippedNames.length) toast.warning(`Could not add: ${skippedNames.join(", ")}`);
        closeModal();
        fetchList();
      } else {
        // One independent breakdown per ticked campus, all sharing the SAME
        // class list (classes aren't per-campus) and the SAME termId (Term
        // is global now -- see TermScope). Session is still a separate
        // document per campus (sharing only its title), so that half still
        // resolves each campus's own matching session by title.
        const sessionTitle = referenceSessionTitle;
        const created: string[] = [];
        const skippedNoSession: string[] = [];
        const skippedNoTerm: string[] = [];
        const skippedExisting: string[] = [];
        const failed: string[] = [];

        for (const branchId of form.branchIds) {
          const branchName = branches.find(b => b._id === branchId)?.name || branchId;
          const ownSession = sessions.find(
            s => s.title === sessionTitle && sessionBranchId(s) === branchId
          );
          if (!ownSession) {
            skippedNoSession.push(branchName);
            continue;
          }
          const scopedToThisBranch = (termsByBranch[branchId] || []).some(t => t._id === form.termId);
          if (!scopedToThisBranch) {
            skippedNoTerm.push(branchName);
            continue;
          }
          const payload: CreateResultBreakdownData & { visibleInApp?: boolean } = {
            title: form.title || autoTitle(form),
            sessionId: ownSession._id,
            branchId,
            systemId: form.systemId,
            termId: form.termId,
            startDate: form.startDate,
            endDate: form.endDate,
            classes: form.classIds,
            status: form.status,
            visibleInApp: form.visibleInApp,
          } as any;
          try {
            await createResultBreakdown(payload);
            created.push(branchName);
          } catch (err: any) {
            const message: string = err?.response?.data?.message || "";
            if (message.includes("overlap")) {
              skippedExisting.push(branchName);
            } else {
              failed.push(`${branchName} (${message || "error"})`);
            }
          }
        }

        if (created.length) toast.success(`Created for: ${created.join(", ")}`);
        if (skippedExisting.length) toast.info(`Already exists, skipped: ${skippedExisting.join(", ")}`);
        if (skippedNoSession.length) toast.warning(`No matching session, skipped: ${skippedNoSession.join(", ")}`);
        if (skippedNoTerm.length) toast.warning(`No matching term, skipped: ${skippedNoTerm.join(", ")}`);
        if (failed.length) toast.error(`Failed: ${failed.join(", ")}`);

        if (created.length || skippedExisting.length) {
          closeModal();
          fetchList();
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error saving breakdown");
    } finally {
      setSaving(false);
    }
  };

  // ── Submit for approval (active/draft → submitted) ────────────────────────
  const handleSubmit = async (rec: ResultBreakdown) => {
    try {
      await submitApproval(rec._id);
      toast.success("Submitted for approval");
      fetchList();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Error submitting for approval");
    }
  };

  // ── Delete (toggle inactive) ──────────────────────────────────────────────
  const handleDelete = async (rec: ResultBreakdown) => {
    try {
      await updateResultBreakdown(rec._id, { status: "inactive" } as any);
      toast.success("Breakdown removed");
      fetchList();
    } catch {
      toast.error("Error removing breakdown");
    }
  };

  // ── Delete/Submit confirmation (replaces native window.confirm) ───────────
  const [pendingAction, setPendingAction] = useState<{ type: "delete" | "submit"; rec: ResultBreakdown } | null>(null);
  const confirmPendingAction = () => {
    if (!pendingAction) return;
    const { type, rec } = pendingAction;
    setPendingAction(null);
    if (type === "delete") handleDelete(rec);
    else handleSubmit(rec);
  };

  // ── Class checkbox toggle ──────────────────────────────────────────────────
  const toggleClassId = (id: string) => {
    const next = form.classIds.includes(id)
      ? form.classIds.filter(c => c !== id)
      : [...form.classIds, id];
    updateField("classIds", next);
  };

  // ── Campus checkbox toggle ──────────────────────────────────────────────
  // "New" mode: free multi-select, creates one breakdown per ticked campus.
  // "Edit" mode: the record being edited itself can't be unticked (that's
  // what the list page's own Delete is for) -- but OTHER campuses can now
  // be ticked/unticked to create or deactivate their matching breakdown
  // alongside this one, since most were created together with the same
  // settings anyway (see handleSave's edit branch).
  const toggleBranch = (id: string) => {
    if (modalMode === "edit" && id === toId(editing?.branchId)) return;
    const next = form.branchIds.includes(id)
      ? form.branchIds.filter(b => b !== id)
      : [...form.branchIds, id];
    updateField("branchIds", next);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toId = (v: any): string =>
    v && typeof v === "object" && v._id ? String(v._id) : typeof v === "string" ? v : "";

  const resolve = (v: any, list: Array<{ _id: string; name?: string; title?: string }>) => {
    const id = toId(v) || (typeof v === "string" ? v : "");
    if (v && typeof v === "object") return v.name ?? v.title ?? id;
    return list.find(i => i._id === id)?.name ?? (list as any[]).find(i => i._id === id)?.title ?? id;
  };

  // Campus list needs Board System picked first; Classes need Board +
  // at least one Campus ticked (both scoped to the chosen Session too).
  const showBranchHint = !form.systemId;
  const showClassHint = !form.systemId || form.branchIds.length === 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-flex align-items-start justify-content-between mb-4">
            <div>
              <h3 className="page-title mb-1">Result breakdowns</h3>
              <p className="text-muted small mb-0">
                Each breakdown ties together session + campus + board system + classes + term.
              </p>
            </div>
            <button className="btn btn-dark" onClick={openNew}>
              + New breakdown
            </button>
          </div>

          {/* Filters */}
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <label className="form-label mb-1">Session</label>
              <select
                className="form-select"
                value={filterSession}
                onChange={e => { setFilterSession(e.target.value); setPage(1); }}
              >
                <option value="">— Any —</option>
                {uniqueSessionsByTitle.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1">Campus</label>
              <select
                className="form-select"
                value={filterBranch}
                onChange={e => { setFilterBranch(e.target.value); setPage(1); }}
              >
                <option value="">— Any —</option>
                {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1">Board</label>
              <select
                className="form-select"
                value={filterSystem}
                onChange={e => { setFilterSystem(e.target.value); setPage(1); }}
              >
                <option value="">— Any —</option>
                {systemsForFilter.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1">Term</label>
              <select
                className="form-select"
                value={filterTerm}
                onChange={e => { setFilterTerm(e.target.value); setPage(1); }}
              >
                <option value="">— Any —</option>
                {termsForFilter.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover bg-white">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Campus</th>
                  <th>Board</th>
                  <th>Term</th>
                  <th>Session</th>
                  <th>Classes</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-4"><span className="spinner-border spinner-border-sm" /></td></tr>
                ) : records.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted py-4">No breakdowns found</td></tr>
                ) : records.map(rec => {
                  const classArr: any[] = Array.isArray(rec.classes) ? rec.classes : [];
                  return (
                    <tr key={rec._id}>
                      <td>
                        <Link to="#" className="text-primary fw-medium" onClick={() => openEdit(rec)}>
                          {rec.title}
                        </Link>
                      </td>
                      <td>{resolve(rec.branchId, branches as any[])}</td>
                      <td>{resolve(rec.systemId, systems)}</td>
                      <td>{resolve(rec.termId, terms as any[])}</td>
                      <td>{resolve(rec.sessionId, sessions as any[])}</td>
                      <td>
                        {classArr.map((c: any, i: number) => (
                          <span key={i} className="me-1">
                            {typeof c === "object" ? c.name : (classes.find(cl => cl._id === c)?.name ?? c)}
                            {i < classArr.length - 1 ? "," : ""}
                          </span>
                        ))}
                      </td>
                      <td>
                        <span className={`badge ${rec.status === "active" ? "bg-primary bg-opacity-10 text-primary" : rec.status === "draft" ? "bg-secondary bg-opacity-10 text-secondary" : "bg-danger bg-opacity-10 text-danger"}`}>
                          {rec.status}
                        </span>
                      </td>
                      <td>
                        <Link to="#" className="text-primary me-2" onClick={() => openEdit(rec)}>Edit</Link>
                        {(rec.status === "active" || rec.status === "draft" || rec.status === "needs_revision") && (
                          <Link to="#" className="text-success me-2" onClick={() => setPendingAction({ type: "submit", rec })}>Submit</Link>
                        )}
                        <Link to="#" className="text-danger" onClick={() => setPendingAction({ type: "delete", rec })}>Delete</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-2">
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalMode && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)", position: "fixed", inset: 0, overflowY: "auto" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalMode === "new" ? "New breakdown" : "Edit breakdown"}</h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>
              <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {/* Other campuses' records from the same multi-campus
                      create -- each campus is still its own independent
                      record (different status/progress), so this is a
                      quick way to jump between them rather than a shared
                      edit across campuses. */}
                  {modalMode === "edit" && (siblingsLoading || siblings.length > 0) && (
                    <div className="alert alert-light border mb-3 py-2">
                      <small className="text-muted d-block mb-1">
                        Also created with these settings:
                      </small>
                      {siblingsLoading ? (
                        <small className="text-muted">Checking…</small>
                      ) : (
                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge bg-primary-subtle text-primary">
                            {branches.find(b => b._id === toId(editing?.branchId))?.name || "This campus"} ({editing?.status}) — this one
                          </span>
                          {siblings.map(s => (
                            <button
                              key={s._id}
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => openEdit(s)}
                            >
                              {branches.find(b => b._id === toId(s.branchId))?.name || s.branch?.name} ({s.status})
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="row g-3">
                    {/* Session -- selected by TITLE, same reason as the
                        session-resolution logic below: Session is a
                        separate document per campus, so the dropdown's
                        deduped "representative" id for a title often isn't
                        THIS record's own session document. In "new" mode
                        any representative is fine (handleSave resolves each
                        ticked campus's own session by title anyway); in
                        "edit" mode the campus is fixed, so picking a title
                        must resolve back to THIS campus's own session doc,
                        not silently reassign the record to a different
                        campus's session with the same title. */}
                    <div className="col-md-6">
                      <label className="form-label">Session</label>
                      <select
                        className="form-select"
                        value={sessions.find(s => s._id === form.sessionId)?.title || ""}
                        onChange={e => {
                          const title = e.target.value;
                          if (modalMode === "edit") {
                            const ownSession = sessions.find(
                              s => s.title === title && sessionBranchId(s) === form.branchIds[0]
                            );
                            updateField("sessionId", ownSession?._id || "");
                          } else {
                            const representative = uniqueSessionsByTitle.find(s => s.title === title);
                            updateField("sessionId", representative?._id || "");
                          }
                        }}
                        required
                      >
                        <option value="">— Select session —</option>
                        {uniqueSessionsByTitle.map(s => <option key={s._id} value={s.title}>{s.title}</option>)}
                      </select>
                      {modalMode === "edit" && !form.sessionId && (
                        <small className="text-danger">This campus has no session with that title.</small>
                      )}
                    </div>

                    {/* Board system */}
                    <div className="col-md-6">
                      <label className="form-label">Board system</label>
                      <select
                        className="form-select"
                        value={form.systemId}
                        onChange={e => updateField("systemId", e.target.value)}
                      >
                        <option value="">— Select board —</option>
                        {systemsForForm.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                      </select>
                    </div>

                    {/* Campus (multi-select always -- in "edit" mode, ticking
                        another campus creates its own matching breakdown
                        alongside this one on Save; unticking an existing
                        one deactivates it. The record actually being
                        edited stays locked on.) */}
                    <div className="col-12">
                      <label className="form-label">
                        Campus{modalMode === "new" ? " (pick one or more)" : ""}
                      </label>
                      {showBranchHint ? (
                        <div className="form-control text-muted" style={{ minHeight: 42 }}>
                          Pick a board system first.
                        </div>
                      ) : (
                        <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: "auto" }}>
                          {branchesForForm.length === 0 ? (
                            <span className="text-muted small">No campuses offer this board</span>
                          ) : branchesForForm.map(b => (
                            <div key={b._id} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`branch-${b._id}`}
                                checked={form.branchIds.includes(b._id)}
                                disabled={modalMode === "edit" && b._id === toId(editing?.branchId)}
                                onChange={() => toggleBranch(b._id)}
                              />
                              <label className="form-check-label" htmlFor={`branch-${b._id}`}>
                                {b.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                      {modalMode === "new" && (
                        <small className="text-muted">
                          Creates one independent breakdown per campus ticked.
                        </small>
                      )}
                    </div>

                    {/* Term is global now -- form.termId IS the id every
                        ticked campus will use directly, no per-campus
                        resolution needed (unlike Session). handleSave still
                        skips a campus individually if this term isn't
                        actually scoped to it (see termsByBranch). */}
                    <div className="col-md-6">
                      <label className="form-label">Term</label>
                      <select
                        className="form-select"
                        value={form.termId}
                        onChange={e => updateField("termId", e.target.value)}
                        disabled={!form.systemId || form.branchIds.length === 0}
                        required
                      >
                        <option value="">— Select term —</option>
                        {termsForForm.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
                      </select>
                      {!form.systemId ? (
                        <small className="text-muted">Pick a board system first.</small>
                      ) : form.branchIds.length === 0 ? (
                        <small className="text-muted">Pick at least one campus first.</small>
                      ) : termsForForm.length === 0 ? (
                        <small className="text-muted">No terms scoped to the ticked campus(es) for this board yet -- set it up in Term Management.</small>
                      ) : null}
                    </div>

                    {/* Start date */}
                    <div className="col-md-6">
                      <label className="form-label">Start date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.startDate}
                        onChange={e => updateField("startDate", e.target.value)}
                      />
                    </div>

                    {/* End date */}
                    <div className="col-md-6">
                      <label className="form-label">End date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.endDate}
                        onChange={e => updateField("endDate", e.target.value)}
                      />
                    </div>

                    {/* Classes (shared catalog by Board System -- same list for every campus ticked) */}
                    <div className="col-12">
                      <label className="form-label">Classes</label>
                      {showClassHint ? (
                        <div className="form-control text-muted" style={{ minHeight: 42 }}>
                          Pick a board system and at least one campus to see classes.
                        </div>
                      ) : (
                        <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                          {classOptionsForForm.length === 0 ? (
                            <span className="text-muted small">No classes available</span>
                          ) : classOptionsForForm.map(c => (
                            <div key={c._id} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`cls-${c._id}`}
                                checked={form.classIds.includes(c._id)}
                                onChange={() => toggleClassId(c._id)}
                              />
                              <label className="form-check-label" htmlFor={`cls-${c._id}`}>
                                {c.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <div className="col-12">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.title}
                        placeholder="First Term Examination (Class 1 & 2) 2025-2026"
                        onChange={e => setForm(prev => ({ ...prev, title: e.target.value, titleOverridden: e.target.value !== "" }))}
                      />
                      {modalMode === "edit" && form.titleOverridden
                        ? <small className="text-muted">Auto-generated. Edit to override (clear the field to re-enable auto-fill).</small>
                        : <small className="text-muted">Pick term + classes + session above to auto-generate a title.</small>
                      }
                    </div>

                    {/* Visible in app -- hidden per user request (2026-08-31):
                        nothing else in the app actually reads this flag yet,
                        so exposing it was misleading. Field/payload left
                        intact in case something wires it up later. */}
                    {/* <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="visibleInApp"
                          checked={form.visibleInApp}
                          onChange={e => updateField("visibleInApp", e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="visibleInApp">
                          Visible in app
                        </label>
                      </div>
                    </div> */}

                    {/* Status */}
                    <div className="col-12">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={form.status}
                        onChange={e => updateField("status", e.target.value as FormData["status"])}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light me-2" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-dark" disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingAction}
        title={pendingAction?.type === "delete" ? "Delete Breakdown?" : "Submit for Approval?"}
        body={
          pendingAction?.type === "delete"
            ? `Delete "${pendingAction.rec.title}"? This cannot be undone.`
            : `Submit "${pendingAction?.rec.title}" for approval? Teachers can no longer edit until it's reviewed.`
        }
        danger={pendingAction?.type === "delete" ? "Yes, Delete" : "Yes, Submit"}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />
    </>
  );
};

export default ResultBreakdowns;
